/**
 * Cross-Tenant Pledge Hooks
 *
 * React Query hooks for fetching and managing pledge proposals across all tenants.
 * Pledges originate in the consumer domain; this admin view reads cross-domain
 * projections via GET /api/admin/pledges.
 *
 * Types derived from platform-admin.openapi.yaml via @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformAdminClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/error-message';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type PledgeStatus = components['schemas']['PledgeStatus'];
export type PledgeSummary = components['schemas']['PledgeSummary'];
export type PledgeDetail = components['schemas']['PledgeDetail'];
export type PledgeListResponse = components['schemas']['PledgeListResponse'];
export type PledgeDetailResponse = components['schemas']['PledgeDetailResponse'];
export type PledgeSelectedAccountDetail = components['schemas']['PledgeSelectedAccountDetail'];
export type PledgeSelectedHoldingDetail = components['schemas']['PledgeSelectedHoldingDetail'];
export type PledgeConsentProof = components['schemas']['PledgeConsentProof'];

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const PLEDGES_KEY = 'pledges';

// ---------------------------------------------------------------------------
// API helpers -- thin wrappers over the typed openapi-fetch client
// ---------------------------------------------------------------------------

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

const pledgesApi = {
  list: async (params?: {
    status?: PledgeStatus;
    page?: number;
    limit?: number;
  }): Promise<PledgeListResponse> => {
    const result = await platformAdminClient.GET('/pledges', {
      params: {
        query: {
          status: params?.status,
          page: params?.page,
          limit: params?.limit,
        },
      },
    });
    return unwrap(result);
  },

  get: async (pledgeId: string): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.GET('/pledges/{pledgeId}', {
      params: { path: { pledgeId } },
    });
    return unwrap(result);
  },

  acknowledge: async (
    pledgeId: string,
    body?: { notes?: string },
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.PATCH('/pledges/{pledgeId}/acknowledge', {
      params: { path: { pledgeId } },
      body: body ?? {},
    });
    return unwrap(result);
  },
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook for listing pledge proposals with optional filtering and pagination.
 */
export function usePledges(params?: {
  status?: PledgeStatus;
  page?: number;
  limit?: number;
}) {
  return useQuery<PledgeListResponse, Error>({
    queryKey: [PLEDGES_KEY, 'list', params],
    queryFn: () => pledgesApi.list(params),
  });
}

/**
 * Hook for fetching a single pledge with full account and consent detail.
 * Only executes when pledgeId is defined.
 */
export function usePledgeDetail(pledgeId: string | undefined) {
  return useQuery<PledgeDetailResponse, Error>({
    queryKey: [PLEDGES_KEY, 'detail', pledgeId],
    queryFn: () => pledgesApi.get(pledgeId as string),
    enabled: !!pledgeId,
  });
}

/**
 * Mutation hook for acknowledging a PENDING pledge.
 * Invalidates both the list and the specific pledge detail on success.
 */
export function useAcknowledgePledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; notes?: string }
  >({
    mutationFn: ({ pledgeId, notes }) => pledgesApi.acknowledge(pledgeId, { notes }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PLEDGES_KEY, 'list'] });
      queryClient.invalidateQueries({
        queryKey: [PLEDGES_KEY, 'detail', variables.pledgeId],
      });
    },
  });
}
